import { noop } from 'lodash';
import React from 'react';
import styled, { css } from 'styled-components';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import { blueLink } from '@zocdoc/branding/colors';
import { BEFORE_TIMEOUT, LINE_HEIGHT } from './constants';

/**
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-link'
 */
export function determineTarget(ev = {}, target = '_self') {
    const windowsCtrlKey = ev.ctrlKey;
    const macOsCmdKey = ev.metaKey;
    const mouseMiddleButton = ev.button === 1;

    if (windowsCtrlKey || macOsCmdKey || mouseMiddleButton) {
        return '_blank';
    } else {
        return target;
    }
}

/**
 * @typedef {object} Props
 * @prop {boolean} enableCalendar
 * @prop {function(): void} beforeClick
 * @prop {string} href
 * @prop {function(): void} onClick
 * @prop {string} target
 *
 * @extends {Component<Props>}
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-link'
 */
class LinkWithFireAndFollowStrategy extends React.Component {
    static defaultProps = {
        beforeClick: noop,
        onClick: noop,
    };

    maybeOpen = (href, target) => {
        if (href) {
            window.open(href, target);
        }
    };

    followLinkOnError = (href, target, err) => {
        // always follow link in production
        if (process.env.NODE_ENV === 'production') {
            this.maybeOpen(href, target);
        } else {
            throw err;
        }
    };

    // "Fire Then Follow Link" strategy, see below:
    handleClick = (ev) => {
        const { beforeClick, onClick, href, target } = this.props;

        this.runClickFn(onClick, ev);

        ev.preventDefault();

        const actualTarget = determineTarget(ev, target);
        const beforePromise = Promise.resolve(this.runClickFn(beforeClick, ev));

        return timeout(beforePromise, BEFORE_TIMEOUT).then(
            () => this.maybeOpen(href, actualTarget),
            (err) => this.followLinkOnError(href, actualTarget, err)
        );
    };

    runClickFn(clickFn, ev) {
        try {
            return clickFn(ev);
        } catch (e) {
            return null;
        }
    }

    render() {
        const { children, ...props } = this.props;

        return (
            <a
                {...filterToValidAttributes(props)}
                onClick={this.handleClick}
                role={props.href ? undefined : 'button'}
                tabIndex="0"
            >
                {children}
            </a>
        );
    }
}

// Implementation of the "Fire Then Follow Link" strategy:
/**
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-link'
 */
export const wait = (ms) =>
    new Promise((resolve, reject) => {
        setTimeout(() => reject(new Error('Timeout on before()')), ms);
    });

/**
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-link'
 */
export const timeout = (promise, ms) => Promise.race([promise, wait(ms)]);

/**
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-link'
 */
export const linkStyle = css`
    background: transparent;
    border: none;
    color: ${blueLink};
    cursor: pointer;
    display: inline;
    line-height: ${LINE_HEIGHT};
    margin: 0;
    padding: 0;
    border-bottom: 1px dotted;

    &:hover,
    &:focus {
        border-bottom-style: solid;
    }
`;

/**
 * @deprecated Use the mezzanine component '@zocdoc-mezzanine/components-link'
 */
const Link = styled(LinkWithFireAndFollowStrategy)`
    ${linkStyle}
`;

export { Link as default, Link, LinkWithFireAndFollowStrategy as UnstyledLink };
